import React from "react";
import {
  Bell,
  CircleHelp,
  Plus,
  Settings,
  Search,
  Ticket,
  LayoutDashboard,
  Calendar,
  Building2,
  User,
  LogOut,
} from "lucide-react";
import { Menu } from "@mantine/core";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./styles.scss";
import { AppName } from "../../lib/data";
import { useDashboardContext } from "../../context/DashboardContext";

const QUICK_ACTIONS = [
  { icon: Search, label: "Search" },
  { icon: Bell, label: "Notifications" },
  { icon: CircleHelp, label: "Need help" },
  { icon: Settings, label: "Settings" },
];

export default function Navbar() {
  const { profile, isLoading } = useDashboardContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("t");
    localStorage.removeItem("r");
    navigate("/login");
  };

  const displayName = profile
    ? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim()
    : isLoading
    ? "Loading..."
    : "Aisewave user";
  const initials =
    (profile?.first_name?.charAt(0) ?? "") +
      (profile?.last_name?.charAt(0) ?? "") || "AW";

  const brandLabel = AppName.toUpperCase();
  const brandAccentIndex = Math.ceil(brandLabel.length / 2);
  const brandPrimary = brandLabel.slice(0, brandAccentIndex);
  const brandSecondary = brandLabel.slice(brandAccentIndex);

  const menuItems = [
    {
      icon: Calendar,
      label: "Events",
      path: "/d",
      active:
        location.pathname === "/d" || location.pathname.startsWith("/d/events"),
    },
    {
      icon: LayoutDashboard,
      label: "Overview",
      path: "/d/overview",
      active: location.pathname === "/d/overview",
    },
    {
      icon: Building2,
      label: "Organizations",
      path: "/d/organizations",
      active:
        location.pathname === "/d/organizations" ||
        location.pathname.startsWith("/d/organizations/"),
    },
    {
      icon: User,
      label: "Profile",
      path: "/d/profile",
      active: location.pathname === "/d/profile",
    },
  ];

  return (
    <header className="navbar-shell">
      <nav className="eventzilla-navbar">
        <div
          className="eventzilla-navbar__brand"
          aria-label={`${AppName} dashboard`}
        >
          <span className="brand-icon" aria-hidden>
            <Ticket className="icon" />
          </span>
          <span className="brand-copy">
            <span className="brand-copy__primary">{brandPrimary}</span>
            <span className="brand-copy__secondary">{brandSecondary}</span>
          </span>
        </div>

        <div className="eventzilla-navbar__actions">
          <Link to="/d/events/new">
            {" "}
            <button className="create-event-btn">
              <Plus className="icon" aria-hidden />
              <span>Create New Event</span>
            </button>{" "}
          </Link>

          <div className="action-cluster">
            {QUICK_ACTIONS.map(({ icon: Icon, label }) => (
              <button
                key={label}
                aria-label={label}
                className="icon-pill"
                type="button"
              >
                <Icon className="icon" aria-hidden />
              </button>
            ))}

            <Menu
              shadow="xl"
              width={280}
              position="bottom-end"
              offset={8}
              withArrow
              arrowPosition="center"
              styles={{
                dropdown: {
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "8px",
                  boxShadow:
                    "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset",
                  padding: "8px",
                },
                arrow: {
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)",
                },
                item: {
                  borderRadius: "8px",
                  padding: "8px 14px",
                  margin: "2px 0",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "rgba(23, 38, 90, 0.1)",
                    transform: "translateX(4px)",
                  },
                },
                itemLabel: {
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                },
              }}
            >
              <Menu.Target>
                <button className="navbar-profile" type="button">
                  <div
                    className="navbar-profile__avatar"
                    aria-hidden={!profile}
                  >
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt={displayName} />
                    ) : (
                      <span className="navbar-profile__initials">
                        {initials.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="sr-only">
                    Open profile menu for {displayName}
                  </span>
                </button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label
                  style={{
                    padding: "12px 16px 8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  Navigation
                </Menu.Label>
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Menu.Item
                      key={item.path}
                      leftSection={<Icon size={18} />}
                      onClick={() => navigate(item.path)}
                      style={{
                        ...(item.active && {
                          background: "rgba(23, 38, 90, 0.15)",
                          color: "#17265a",
                          fontWeight: 600,
                        }),
                      }}
                    >
                      {item.label}
                    </Menu.Item>
                  );
                })}
                <Menu.Divider style={{ margin: "8px 0" }} />
                <Menu.Label
                  style={{
                    padding: "8px 16px 8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  {displayName}
                </Menu.Label>
                <Menu.Item
                  leftSection={<LogOut size={18} />}
                  onClick={handleLogout}
                  color="red"
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>
      </nav>
    </header>
  );
}
